import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { alertKey } from '@wodi/domain';
import type {
  FormularyOptionView,
  FrequencyView,
  PrescribingAlertView,
  PrescriptionCheckView,
  PrescriptionView,
} from '@wodi/shared';
import {
  AlertModal,
  AlertPanel,
  Button,
  Checkbox,
  DoseField,
  LoadingBlock,
  Notice,
  RadioGroup,
  SearchPicker,
  Select,
  Tag,
  TextField,
  useShortcut,
  type SearchPickerStatus,
} from '@wodi/ui';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { patientBannerQuery } from '../api/patients.ts';
import {
  checkPrescription,
  frequenciesQuery,
  prescribingKeys,
  searchFormulary,
  signPrescription,
} from '../api/prescribing.ts';
import { useOnline } from '../hooks/use-online.ts';
import { REASON_MIN_LENGTH } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import {
  draftProblems,
  drugDisplay,
  newDraft,
  quantityWords,
  toApiLine,
  type DraftProblem,
  type DraftState,
} from './draft.ts';
import { alertWords, INTERACTIONS_NOT_CHECKED, type AlertWordsContext } from './messages.ts';
import styles from './prescribing.module.css';

// The prescribing panel (docs/plans/phase1-consultation.md §7.3), self-contained so the
// consultation workspace can mount it. Every change is checked on the server (debounced
// POST …/check); alerts appear on the line in their tier: passive markers, interruptive
// panels that need a reason, and a blocking modal whose only primary action is the safe
// one. The server re-checks everything at signing; the client never decides a tier.
// Drug interactions are not checked, and the foot of the panel always says so.

const CHECK_DEBOUNCE_MS = 400;
const SEARCH_DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

const PROBLEM_WORDS: Record<DraftProblem, string> = {
  dose: 'Enter the dose as a number above zero.',
  route: 'Choose the route.',
  frequency: 'Choose how often.',
  max_doses: 'Enter the most doses allowed in 24 hours.',
  duration: 'Enter how many days or weeks.',
  quantity: 'The quantity is a whole number above zero, or leave it empty.',
};

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, ms]);
  return debounced;
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function stockWords(option: FormularyOptionView): string {
  if (option.stock.state === 'not_stocked') return 'Buy outside';
  if (option.stock.state === 'out_of_stock') return 'Out of stock here';
  return `${String(option.stock.usable ?? 0)} ${option.baseUnit?.display ?? ''} in pharmacy`.trim();
}

function DrugPicker({
  visitId,
  onSelect,
}: {
  visitId: string;
  onSelect: (o: FormularyOptionView) => void;
}) {
  const online = useOnline();
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const debounced = useDebounced(trimmed, SEARCH_DEBOUNCE_MS);
  const search = useQuery({
    queryKey: ['formulary-search', visitId, debounced],
    queryFn: () => searchFormulary(visitId, debounced),
    enabled: online && debounced.length >= MIN_CHARS,
    staleTime: 30_000,
    retry: false,
  });
  let status: SearchPickerStatus;
  if (trimmed.length < MIN_CHARS) status = 'idle';
  else if (!online) status = 'offline';
  else if (debounced !== trimmed || search.isFetching || search.isPending) status = 'searching';
  else if (search.isError) status = 'error';
  else status = 'ready';
  return (
    <SearchPicker
      label="Add drug"
      placeholder="Generic or brand name"
      inputValue={text}
      onInputChange={setText}
      status={status}
      items={search.data ?? []}
      itemsQuery={debounced}
      getKey={(o) => o.formularyItemId}
      getTextValue={(o) => `${drugDisplay(o)} ${o.brandName ?? ''}`}
      renderItem={(o) => (
        <span className={styles.pickerRow}>
          <span className={styles.pickerName}>{o.genericName}</span>
          <span>{`${o.strengthDisplay} ${o.doseForm.display.toLowerCase()}`}</span>
          {o.brandName === null ? null : <span className={styles.pencil}>{o.brandName}</span>}
          <span className={styles.pencil}>{stockWords(o)}</span>
          {o.isControlled ? <Tag state="neutral">Controlled</Tag> : null}
        </span>
      )}
      onSelect={(o) => {
        setText('');
        onSelect(o);
      }}
      promptMessage="Type at least two letters of the drug name."
      emptyMessage="No drug in the formulary matches. Check the spelling, or ask the pharmacist to add it."
      {...(search.error === null
        ? {}
        : { errorMessage: errorText(search.error, 'Search did not work. Try again.') })}
      onRetry={() => void search.refetch()}
      offlineMessage="Searching the formulary needs a connection to Wodi."
      countMessage={(n) => `${String(n)} ${n === 1 ? 'drug' : 'drugs'} found`}
    />
  );
}

function PassiveMarkers({
  alerts,
  context,
}: {
  alerts: readonly PrescribingAlertView[];
  context: AlertWordsContext;
}) {
  const passive = alerts.filter((a) => a.tier === 'passive');
  if (passive.length === 0) return null;
  return (
    <ul className={styles.passive} aria-label="Notes">
      {passive.map((a, i) => (
        <li key={`${a.ruleId}-${String(i)}`}>{alertWords(a, context).title}</li>
      ))}
    </ul>
  );
}

interface InterruptiveProps {
  alerts: readonly PrescribingAlertView[];
  context: AlertWordsContext;
  reasons: ReadonlyMap<string, string>;
  onReason: (key: string, reason: string) => void;
  showErrors: boolean;
}

/** One panel per rule and line: acknowledging covers every alert of that rule there. */
function InterruptivePanels({ alerts, context, reasons, onReason, showErrors }: InterruptiveProps) {
  const groups = new Map<string, PrescribingAlertView[]>();
  for (const a of alerts) {
    if (a.tier !== 'interruptive') continue;
    const key = alertKey(a.ruleId, a.lineKey);
    groups.set(key, [...(groups.get(key) ?? []), a]);
  }
  return (
    <>
      {[...groups].map(([key, group]) => {
        const [first] = group;
        if (first === undefined) return null;
        const words = group.map((a) => alertWords(a, context));
        const reason = reasons.get(key) ?? '';
        const short = reason.trim().length < REASON_MIN_LENGTH;
        return (
          <AlertPanel
            key={key}
            title={words[0]?.title ?? ''}
            danger={first.ruleId.startsWith('rx.allergy.match')}
            reason={reason}
            onReasonChange={(value) => {
              onReason(key, value);
            }}
            reasonLabel="Reason for continuing"
            {...(showErrors && short
              ? {
                  reasonError: `Give a reason of at least ${String(REASON_MIN_LENGTH)} characters.`,
                }
              : {})}
          >
            {words.map((w, i) => (
              <p key={i} className={styles.alertDetail}>
                {i === 0 ? w.detail : [w.title, w.detail].filter(Boolean).join(' ')}
              </p>
            ))}
          </AlertPanel>
        );
      })}
    </>
  );
}

function LineEditor({
  draft,
  frequencies,
  check,
  context,
  reasons,
  onReason,
  showErrors,
  onChange,
  onRemove,
}: {
  draft: DraftState;
  frequencies: readonly FrequencyView[];
  check: PrescriptionCheckView['lines'][number] | undefined;
  context: AlertWordsContext;
  reasons: ReadonlyMap<string, string>;
  onReason: (key: string, reason: string) => void;
  showErrors: boolean;
  onChange: (next: DraftState) => void;
  onRemove: () => void;
}) {
  const problems = draftProblems(draft, frequencies);
  const shown = (p: DraftProblem) =>
    showErrors && problems.includes(p) ? { errorMessage: PROBLEM_WORDS[p] } : {};
  const frequency = frequencies.find((f) => f.code === draft.frequencyCode);
  const set = (patch: Partial<DraftState>) => {
    onChange({ ...draft, ...patch });
  };
  const option = draft.option;
  const unitOptions = option.doseUnitOptions;
  const calculated = check?.quantityCalculated ?? null;
  return (
    <section className={styles.line} aria-label={drugDisplay(option)}>
      <header className={styles.lineHeader}>
        <h2 className={styles.lineTitle}>{drugDisplay(option)}</h2>
        {option.brandName === null ? null : (
          <span className={styles.pencil}>{option.brandName}</span>
        )}
        <Button variant="quiet" onPress={onRemove}>
          Remove
        </Button>
      </header>
      <div className={styles.fields}>
        <DoseField
          label="Dose"
          unit={unitOptions.find((u) => u.code === draft.doseUnit)?.display ?? draft.doseUnit}
          value={draft.dose}
          onChange={(dose) => {
            set({ dose });
          }}
          {...shown('dose')}
        />
        {unitOptions.length > 1 ? (
          <RadioGroup
            label="Dose in"
            orientation="horizontal"
            value={draft.doseUnit}
            onChange={(doseUnit) => {
              set({ doseUnit });
            }}
            options={unitOptions.map((u) => ({ id: u.code, label: u.display }))}
          />
        ) : null}
        {option.routes.length <= 6 ? (
          <RadioGroup
            label="Route"
            orientation="horizontal"
            value={draft.routeCode}
            onChange={(routeCode) => {
              set({ routeCode });
            }}
            options={option.routes.map((r) => ({ id: r.code, label: r.display }))}
            {...shown('route')}
          />
        ) : (
          <Select
            label="Route"
            value={draft.routeCode === '' ? null : draft.routeCode}
            onChange={(key) => {
              set({ routeCode: String(key) });
            }}
            options={option.routes.map((r) => ({ id: r.code, label: r.display }))}
            {...shown('route')}
          />
        )}
      </div>
      <RadioGroup
        label="How often"
        orientation="horizontal"
        value={draft.frequencyCode}
        onChange={(frequencyCode) => {
          set({ frequencyCode });
        }}
        options={frequencies
          .filter((f) => f.status === 'active')
          .map((f) => ({
            id: f.code,
            label: f.abbreviation === null ? f.display : `${f.display} (${f.abbreviation})`,
          }))}
        {...shown('frequency')}
      />
      <div className={styles.fields}>
        {frequency?.kind === 'as_needed' ? (
          <TextField
            label="Most doses in 24 hours"
            inputMode="numeric"
            value={draft.maxDosesPerDay}
            onChange={(maxDosesPerDay) => {
              set({ maxDosesPerDay });
            }}
            {...shown('max_doses')}
          />
        ) : null}
        {frequency?.kind === 'once' ? null : (
          <>
            <TextField
              label="For"
              inputMode="numeric"
              value={draft.durationValue}
              onChange={(durationValue) => {
                set({ durationValue });
              }}
              {...shown('duration')}
            />
            <RadioGroup
              label="Days or weeks"
              orientation="horizontal"
              value={draft.durationUnit}
              onChange={(value) => {
                set({ durationUnit: value === 'weeks' ? 'weeks' : 'days' });
              }}
              options={[
                { id: 'days', label: 'Days' },
                { id: 'weeks', label: 'Weeks' },
              ]}
            />
          </>
        )}
        <TextField
          label="Quantity to dispense"
          inputMode="numeric"
          {...(option.baseUnit === null ? {} : { unit: option.baseUnit.display })}
          value={draft.quantity}
          placeholder={calculated === null ? '' : String(calculated)}
          onChange={(quantity) => {
            set({ quantity });
          }}
          description={
            calculated === null
              ? option.quantityCalculable
                ? 'Worked out once the dose, frequency and duration are complete.'
                : 'Not worked out for this form. Enter the quantity.'
              : `Course: ${quantityWords(calculated, option.baseUnit?.display)}. Leave empty to dispense the course.`
          }
          {...shown('quantity')}
        />
      </div>
      <TextField
        label="Instructions"
        description="Printed on the prescription, e.g. after food."
        value={draft.instructions}
        onChange={(instructions) => {
          set({ instructions });
        }}
      />
      <div className={styles.fields}>
        <Checkbox
          isSelected={draft.substitutionAllowed}
          onChange={(substitutionAllowed) => {
            set({ substitutionAllowed });
          }}
        >
          Allow substitution
        </Checkbox>
        <Checkbox
          isSelected={draft.giveInClinic}
          onChange={(giveInClinic) => {
            set({ giveInClinic });
          }}
        >
          Give in clinic
        </Checkbox>
      </div>
      {check === undefined ? null : (
        <>
          <PassiveMarkers alerts={check.alerts} context={context} />
          <InterruptivePanels
            alerts={check.alerts}
            context={context}
            reasons={reasons}
            onReason={onReason}
            showErrors={showErrors}
          />
        </>
      )}
    </section>
  );
}

export interface PrescribingPanelProps {
  visitId: string;
  patientId: string;
  /** The consultation it is written in, when mounted in the consultation workspace. */
  consultationId?: string | null;
  /** After signing, e.g. to open the print. */
  onSigned?: (prescription: PrescriptionView) => void;
  /** "Review allergy" from a blocking allergy alert, where the workspace offers it. */
  onReviewAllergy?: (allergyId: string) => void;
}

export function PrescribingPanel({
  visitId,
  patientId,
  consultationId = null,
  onSigned,
  onReviewAllergy,
}: PrescribingPanelProps) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const frequencies = useQuery(frequenciesQuery);
  const banner = useQuery(patientBannerQuery(patientId));
  const [drafts, setDrafts] = useState<DraftState[]>([]);
  const [reasons, setReasons] = useState<Map<string, string>>(new Map());
  const [showErrors, setShowErrors] = useState(false);
  /** Interruptive alerts shown so far; ones answered by changing the line are reported. */
  const seen = useRef(
    new Map<string, { ruleId: PrescribingAlertView['ruleId']; lineKey: string | null }>(),
  );
  const nextKey = useRef(1);
  const [clientRequestId, setClientRequestId] = useState(() => crypto.randomUUID());
  const [dismissedBlock, setDismissedBlock] = useState<string | null>(null);

  const frequencyList = useMemo(() => frequencies.data?.items ?? [], [frequencies.data]);
  const complete =
    drafts.length > 0 && drafts.every((d) => draftProblems(d, frequencyList).length === 0);
  const apiLines = useMemo(
    () => (complete ? drafts.map((d) => toApiLine(d, frequencyList)) : null),
    [complete, drafts, frequencyList],
  );
  const debouncedLines = useDebounced(apiLines, CHECK_DEBOUNCE_MS);
  const check = useQuery({
    queryKey: ['prescription-check', visitId, debouncedLines],
    queryFn: () => checkPrescription(visitId, { lines: debouncedLines ?? [] }),
    enabled: online && debouncedLines !== null && debouncedLines.length > 0,
    retry: false,
  });
  const checkCurrent = check.data !== undefined && debouncedLines === apiLines && !check.isFetching;

  const allAlerts = useMemo(
    () =>
      check.data === undefined
        ? []
        : [...check.data.prescriptionAlerts, ...check.data.lines.flatMap((l) => l.alerts)],
    [check.data],
  );
  useEffect(() => {
    for (const a of allAlerts) {
      if (a.tier === 'interruptive')
        seen.current.set(alertKey(a.ruleId, a.lineKey), { ruleId: a.ruleId, lineKey: a.lineKey });
    }
  }, [allAlerts]);

  const context: AlertWordsContext = useMemo(() => {
    const allergies = banner.data?.allergies;
    const names = new Map<string, string>();
    if (allergies?.state === 'list') {
      for (const a of allergies.allergies)
        names.set(a.id, a.reaction === null ? a.substance : `${a.substance} (${a.reaction})`);
    }
    return {
      allergyNames: names,
      lineDrugs: new Map(drafts.map((d) => [d.key, drugDisplay(d.option)])),
    };
  }, [banner.data, drafts]);

  const blocking = allAlerts.filter((a) => a.tier === 'blocking');
  const [firstBlock] = blocking;
  const blockKey =
    firstBlock === undefined ? null : alertKey(firstBlock.ruleId, firstBlock.lineKey);
  const requiredAlerts = new Map(
    allAlerts
      .filter((a) => a.tier === 'interruptive')
      .map(
        (a) => [alertKey(a.ruleId, a.lineKey), { ruleId: a.ruleId, lineKey: a.lineKey }] as const,
      ),
  );
  const required = [...requiredAlerts.keys()];
  const unanswered = required.filter(
    (k) => (reasons.get(k) ?? '').trim().length < REASON_MIN_LENGTH,
  );

  const sign = useMutation({
    mutationFn: () => {
      if (apiLines === null) throw new Error('lines incomplete');
      const acknowledgements = [...requiredAlerts].map(([k, a]) => ({
        ...a,
        reason: (reasons.get(k) ?? '').trim(),
      }));
      const seenAlerts = [...seen.current.entries()]
        .filter(([k]) => !requiredAlerts.has(k))
        .map(([, a]) => ({ ...a, outcome: 'changed_action' as const }));
      return signPrescription(visitId, {
        clientRequestId,
        lines: apiLines,
        acknowledgements,
        seenAlerts,
        consultationId,
      });
    },
    onSuccess: (rx) => {
      toasts.show({ title: 'Prescription signed' });
      setDrafts([]);
      setReasons(new Map());
      setShowErrors(false);
      seen.current = new Map();
      setClientRequestId(crypto.randomUUID());
      void queryClient.invalidateQueries({ queryKey: prescribingKeys.visit(visitId) });
      void queryClient.invalidateQueries({ queryKey: prescribingKeys.medicines(patientId) });
      onSigned?.(rx);
    },
  });

  const canSign =
    online &&
    complete &&
    checkCurrent &&
    blocking.length === 0 &&
    unanswered.length === 0 &&
    !sign.isPending;
  const trySign = () => {
    setShowErrors(true);
    if (canSign) sign.mutate();
  };
  useShortcut('mod+Enter', trySign, {
    allowInFields: true,
    enabled: drafts.length > 0,
    description: 'Sign prescription',
  });

  const addDrug = (option: FormularyOptionView) => {
    const key = `L${String(nextKey.current)}`;
    nextKey.current += 1;
    setDrafts((current) => [...current, newDraft(key, option)]);
  };

  if (frequencies.isPending) return <LoadingBlock label="Loading prescribing" />;
  if (frequencies.isError) {
    return (
      <Notice
        tone="error"
        action={<Button onPress={() => void frequencies.refetch()}>Try again</Button>}
      >
        {errorText(frequencies.error, 'Prescribing could not load. Try again.')}
      </Notice>
    );
  }

  return (
    <section className={styles.panel} aria-label="Prescribe">
      {online ? null : (
        <Notice tone="info">
          Prescribing needs a connection to Wodi. Your lines stay here until you reconnect.
        </Notice>
      )}
      <DrugPicker visitId={visitId} onSelect={addDrug} />
      {drafts.length === 0 ? (
        <p className={styles.empty}>
          No drugs on this prescription yet. Search the formulary to add one.
        </p>
      ) : null}
      {check.data === undefined ? null : (
        <InterruptivePanels
          alerts={check.data.prescriptionAlerts}
          context={context}
          reasons={reasons}
          onReason={(key, value) => {
            setReasons((m) => new Map(m).set(key, value));
          }}
          showErrors={showErrors}
        />
      )}
      {drafts.map((draft) => (
        <LineEditor
          key={draft.key}
          draft={draft}
          frequencies={frequencyList}
          check={checkCurrent ? check.data.lines.find((l) => l.lineKey === draft.key) : undefined}
          context={context}
          reasons={reasons}
          onReason={(key, value) => {
            setReasons((m) => new Map(m).set(key, value));
          }}
          showErrors={showErrors}
          onChange={(next) => {
            setDrafts((current) => current.map((d) => (d.key === next.key ? next : d)));
          }}
          onRemove={() => {
            setDrafts((current) => current.filter((d) => d.key !== draft.key));
          }}
        />
      ))}
      {check.isError ? (
        <Notice tone="error">{errorText(check.error, 'The checks did not run. Try again.')}</Notice>
      ) : null}
      {sign.isError ? (
        <Notice tone="error">
          {errorText(sign.error, 'The prescription was not signed. Try again.')}
        </Notice>
      ) : null}
      {drafts.length === 0 ? null : (
        <div className={styles.actions}>
          <p className={styles.status} role="status">
            {!complete
              ? 'Complete each line to check it.'
              : !checkCurrent
                ? 'Checking…'
                : blocking.length > 0
                  ? 'A line cannot be prescribed. Choose another drug or change the dose.'
                  : unanswered.length > 0
                    ? 'Give a reason for each alert to sign.'
                    : 'Checked.'}
          </p>
          <Button
            variant="primary"
            isPending={sign.isPending}
            pendingLabel="Signing…"
            isDisabled={!online || sign.isPending}
            onPress={trySign}
          >
            Sign prescription
          </Button>
        </div>
      )}
      <p className={styles.foot}>{INTERACTIONS_NOT_CHECKED}</p>
      {firstBlock === undefined ? null : (
        <AlertModal
          isOpen={blockKey !== dismissedBlock}
          onOpenChange={(open) => {
            if (!open) setDismissedBlock(blockKey);
          }}
          title={alertWords(firstBlock, context).title}
          message={
            alertWords(firstBlock, context).detail ??
            'This drug cannot be prescribed for this patient.'
          }
          evidence={firstBlock.provisional ? ['Based on a provisional rule or group.'] : []}
          safeActionLabel="Choose another drug"
          onSafeAction={() => {
            setDrafts((current) => current.filter((d) => d.key !== firstBlock.lineKey));
          }}
          {...(onReviewAllergy !== undefined && typeof firstBlock.facts['allergyId'] === 'string'
            ? {
                secondaryAction: {
                  label: 'Review allergy',
                  onAction: () => {
                    onReviewAllergy(String(firstBlock.facts['allergyId']));
                  },
                },
              }
            : {})}
        />
      )}
    </section>
  );
}
